/**
 * core/schema/runner.ts · 数据库 migration 调度器
 *
 * 复用 v18.9.197 schema-runner 思路 (已在 v200.16.1 生产稳定 1+ 月):
 *   - __tn_migrations 系统表持久化已应用 migration ID
 *   - 每个 migration 独立 .ts 文件 (001_*.ts / 002_*.ts ...)
 *   - 全 CREATE TABLE IF NOT EXISTS + ALTER try/catch (幂等 · 可重复跑)
 *   - 失败不中断 · 记录到 stats.failed
 */

import type { Database } from 'sql.js';
import { migrations } from './migrations';

interface MigrationResult {
  id: string;
  applied: number;
  skipped: number;
  failed: { id: string; error: string }[];
}

export async function runMigrations(db: Database): Promise<MigrationResult> {
  const result: MigrationResult = {
    id: 'v18.9.300',
    applied: 0,
    skipped: 0,
    failed: [],
  };

  // 1. 确保 __tn_migrations 系统表存在
  try {
    db.exec(`
      CREATE TABLE IF NOT EXISTS __tn_migrations (
        id TEXT PRIMARY KEY,
        applied_at TEXT NOT NULL,
        version TEXT
      )
    `);
  } catch (err) {
    console.error('[migrations] init system table FAILED', err);
    return result;
  }

  // 2. 查已应用 migration
  const applied = new Set<string>();
  try {
    const stmt = db.prepare('SELECT id FROM __tn_migrations');
    while (stmt.step()) {
      const row = stmt.getAsObject() as { id: string };
      applied.add(row.id);
    }
    stmt.free();
  } catch {
    // 系统表第一次跑 · 忽略
  }

  // 3. 逐个跑 migration (按 id 排序)
  const sortedIds = Object.keys(migrations).sort();
  for (const id of sortedIds) {
    if (applied.has(id)) {
      result.skipped++;
      continue;
    }
    const fn = migrations[id];
    try {
      fn(db);
      // 标记已应用
      db.run(
        'INSERT INTO __tn_migrations (id, applied_at, version) VALUES (?, ?, ?)',
        [id, new Date().toISOString(), 'v18.9.300']
      );
      result.applied++;
      // eslint-disable-next-line no-console
      console.log(`[migrations] applied ${id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      result.failed.push({ id, error: msg });
      console.error(`[migrations] FAILED ${id}:`, msg);
    }
  }

  // eslint-disable-next-line no-console
  console.log('[migrations] result:', result);
  return result;
}
